import { createBrowserRouter } from 'react-router-dom';
import { AppShell } from '@/components/layout/app-shell';
import { RouteError } from '@/components/route-error';
import { RequireAuth } from '@/features/auth/require-auth';

/**
 * Application routes.
 *
 * The public half — the landing page and the auth dialogs — is mounted at
 * `/`, `/login` and `/reset-password`. Everything behind authentication
 * hangs off a *pathless* layout route, so the workspace keeps the same URLs
 * it always had (`/dashboard`, `/documents`, ...) while `/` stays public.
 *
 * Every screen is code-split, so the first paint on a phone downloads the
 * landing page only. That matters for a PWA opened from a home screen on a
 * slow connection in a stockroom.
 */
export const router = createBrowserRouter([
  {
    path: '/',
    errorElement: <RouteError />,
    lazy: async () => {
      const { PublicAuthPage } = await import('@/features/auth/public-auth-page');
      return { Component: PublicAuthPage };
    },
  },
  {
    path: '/login',
    errorElement: <RouteError />,
    lazy: async () => {
      const { PublicAuthPage } = await import('@/features/auth/public-auth-page');
      return {
        Component: () => <PublicAuthPage defaultDialog="login" />,
      };
    },
  },
  {
    path: '/reset-password',
    errorElement: <RouteError />,
    lazy: async () => {
      const { PublicAuthPage } = await import('@/features/auth/public-auth-page');
      return {
        Component: () => <PublicAuthPage defaultDialog="reset" />,
      };
    },
  },

  /* ------------------------------------------------------------- app --- */
  {
    /* No `path`: this route only contributes the shell and the auth gate. */
    errorElement: <RouteError />,
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      {
        path: 'dashboard',
        lazy: async () => {
          const { DashboardPage } = await import('@/features/dashboard/dashboard-page');
          return { Component: DashboardPage };
        },
      },
      {
        path: 'catalog/products',
        lazy: async () => {
          const { ProductsPage } = await import('@/features/catalog/products-page');
          return { Component: ProductsPage };
        },
      },
      {
        path: 'catalog/categories',
        lazy: async () => {
          const { CategoriesPage } = await import('@/features/catalog/categories-page');
          return { Component: CategoriesPage };
        },
      },
      {
        path: 'catalog/units',
        lazy: async () => {
          const { UnitsPage } = await import('@/features/catalog/units-page');
          return { Component: UnitsPage };
        },
      },
      {
        path: 'warehouses',
        lazy: async () => {
          const { WarehousesPage } = await import('@/features/warehouse/warehouses-page');
          return { Component: WarehousesPage };
        },
      },
      {
        path: 'stock',
        lazy: async () => {
          const { StockPage } = await import('@/features/stock/stock-page');
          return { Component: StockPage };
        },
      },
      {
        path: 'stock/movements',
        lazy: async () => {
          const { MovementsPage } = await import('@/features/stock/movements-page');
          return { Component: MovementsPage };
        },
      },
      {
        path: 'documents',
        lazy: async () => {
          const { DocumentsPage } = await import('@/features/documents/documents-page');
          return { Component: DocumentsPage };
        },
      },
      {
        path: 'documents/new',
        lazy: async () => {
          const { DocumentEditorPage } = await import('@/features/documents/document-editor-page');
          return { Component: DocumentEditorPage };
        },
      },
      {
        path: 'documents/:id',
        lazy: async () => {
          const { DocumentViewPage } = await import('@/features/documents/document-view-page');
          return { Component: DocumentViewPage };
        },
      },
      {
        path: 'documents/:id/edit',
        lazy: async () => {
          const { DocumentEditorPage } = await import('@/features/documents/document-editor-page');
          return { Component: DocumentEditorPage };
        },
      },
      {
        path: 'counterparties',
        lazy: async () => {
          const { CounterpartiesPage } = await import('@/features/counterparties/counterparties-page');
          return { Component: CounterpartiesPage };
        },
      },
      {
        path: 'users',
        lazy: async () => {
          const { UsersPage } = await import('@/features/users/users-page');
          return { Component: UsersPage };
        },
      },
      {
        path: 'settings',
        lazy: async () => {
          const { SettingsPage } = await import('@/features/settings/settings-page');
          return { Component: SettingsPage };
        },
      },
      {
        path: '*',
        lazy: async () => {
          const { NotFoundPage } = await import('@/features/not-found-page');
          return { Component: NotFoundPage };
        },
      },
    ],
  },
]);
